import React, { useCallback, useMemo, useRef, useState } from 'react'
import { EventExpansionPanelSummary } from 'Event/components/EventExpansionPanelSummary'
import { EventExpansionPanelDetails } from 'Event/components/EventExpansionPanelDetails'
import { EventExpansionPanel } from 'Event/components/EventExpansionPanel'
import { useExpansionPanelSummaryStyle } from 'Event/hooks/useExpansionPanelSummaryStyle'
import useEventDrilldownStyles from 'Event/hooks/useEventDrilldownStyles'
import { EventDrilldownSectionHeader } from 'Event/components/EventDrilldownSectionHeader'
import {
  AssociationType,
  EventGroup,
  OpportunityResponseWithRecurrenceInfo,
} from 'Event/interfaces/interfaceCreateEditEvent'
import { EventQuestionSet } from 'Event/hooks/useGetEventQuestionSets'
import { Person } from 'champion/interfaces'
import { useFeatureEnabled } from 'core/feature/hooks'
import SaveAltOutlinedIcon from '@material-ui/icons/SaveAltOutlined'
import IconButton from '@material-ui/core/IconButton'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useDownloadEventQuestionnaireExport } from 'question-sets/hooks'

const BCC_VISIBLE_COUNT = 3

export const EventDrilldownDetails = ({
  event,
  encodedOccurrence,
  scrollTop,
  groups,
  questionSets,
  onEditEventInformation,
  onEditRegistrationDetails,
  showEditActions,
}: {
  event: OpportunityResponseWithRecurrenceInfo
  encodedOccurrence: string
  scrollTop: number
  groups: EventGroup[]
  questionSets: EventQuestionSet[]
  onEditEventInformation: () => void
  onEditRegistrationDetails: () => void
  showEditActions: boolean
}) => {
  const classes = useEventDrilldownStyles()
  const [expanded, setExpanded] = useState(true)
  const [bccExpanded, setBccExpanded] = useState(false)
  const summaryRef = useRef<HTMLDivElement>(null)
  const detailsRef = useRef<HTMLDivElement>(null)
  const isQuestionnaireAsSurveyEnabled = useFeatureEnabled('QuestionnaireAsSurvey')
  const [downloadEventQuestionnaireExport] = useDownloadEventQuestionnaireExport()

  const style = useExpansionPanelSummaryStyle({
    expanded,
    scrollTop,
    summaryRef,
    detailsRef,
  })

  const bccContacts = event.pointOfContactPersons || []
  const visibleBccContacts = bccExpanded ? bccContacts : bccContacts.slice(0, BCC_VISIBLE_COUNT)
  const hasMoreBcc = bccContacts.length > BCC_VISIBLE_COUNT

  const onboardingGroups = useMemo(
    () => groups.filter(({ associationType }) => associationType === AssociationType.ADD_PARTICIPANTS_TO_GROUP),
    [groups],
  )

  const waivers = event.requiredCredentials || []

  const questionnaires = useMemo(
    () =>
      questionSets.map((eventQuestionSet, index) => ({
        order: index + 1,
        id: isQuestionnaireAsSurveyEnabled ? eventQuestionSet.survey?.id : eventQuestionSet.questionSet.id,
        questionSetId: eventQuestionSet.questionSet.id,
        name: eventQuestionSet.questionSet.name,
      })),
    [isQuestionnaireAsSurveyEnabled, questionSets],
  )

  const downloadQuestionnaire = useCallback(
    (questionSetId: number, name: string) => {
      ;(
        downloadEventQuestionnaireExport as (params: {
          encodedOccurrence: string
          questionSetId: number
          name: string
          isQuestionnaireAsSurveyEnabled: boolean
        }) => void
      )({
        encodedOccurrence,
        questionSetId,
        name,
        isQuestionnaireAsSurveyEnabled,
      })
    },
    [downloadEventQuestionnaireExport, encodedOccurrence, isQuestionnaireAsSurveyEnabled],
  )

  const onEditPrimary = showEditActions ? onEditEventInformation : undefined
  const onEditForms = showEditActions ? onEditRegistrationDetails : undefined
  const onEditGroups = showEditActions ? onEditRegistrationDetails : undefined

  return (
    <EventExpansionPanel onChange={(_, panelExpanded) => setExpanded(panelExpanded)}>
      <EventExpansionPanelSummary name="event-details" style={style} ref={summaryRef}>
        <h1>Details</h1>
      </EventExpansionPanelSummary>
      <EventExpansionPanelDetails ref={detailsRef}>
        <div className={classes.detailsSectionLayout}>
          <div className={classes.eventInfo}>
            <EventDrilldownSectionHeader title="Primary Point of Contact" onEdit={onEditPrimary} />
            <div className={classes.sectionRow}>
              <div>Name:</div>
              <div>{event.contact_name}</div>
            </div>
            <div className={classes.sectionRow}>
              <div>Phone:</div>
              <div>{event.contact_phone_number}</div>
            </div>
            <div className={classes.sectionRow}>
              <div>Email:</div>
              <div>{event.contact_email}</div>
            </div>

            <EventDrilldownSectionHeader title="Point of Contact BCC" onEdit={onEditPrimary} />
            {bccContacts.length === 0 ? (
              <div className={classes.detailsEmptyText}>None</div>
            ) : (
              <>
                {visibleBccContacts.map((person: Person) => (
                  <div key={person.id} className={classes.bccContact}>
                    <div>{`${person.givenName} ${person.familyName}`.trim()}</div>
                    <div>{person.email}</div>
                  </div>
                ))}
                {hasMoreBcc && (
                  <button type="button" className={classes.showMoreButton} onClick={() => setBccExpanded(v => !v)}>
                    {bccExpanded ? 'Show less' : 'Show more'}
                    <ExpandMoreIcon className={bccExpanded ? classes.showMoreIconExpanded : classes.showMoreIcon} />
                  </button>
                )}
              </>
            )}
          </div>
          <div className={classes.detailsSectionRightColumn}>
            <div className={classes.detailsPanel}>
              <EventDrilldownSectionHeader title="Required Forms" onEdit={onEditForms} />
              <div className={classes.formsSubsection}>
                <div className={classes.formsSubsectionTitle}>Questionnaires</div>
                {questionnaires.length === 0 ? (
                  <div className={classes.detailsEmptyText}>None</div>
                ) : (
                  <ul className={classes.numberedList}>
                    {questionnaires.map(({ order, questionSetId, name }) => (
                      <QuestionnaireRow
                        key={questionSetId}
                        order={order}
                        name={name}
                        onExport={() => downloadQuestionnaire(questionSetId, name)}
                      />
                    ))}
                  </ul>
                )}
              </div>
              <div className={classes.formsSubsection}>
                <div className={classes.formsSubsectionTitle}>Waivers</div>
                {waivers.length === 0 ? (
                  <div className={classes.detailsEmptyText}>None</div>
                ) : (
                  <ul className={classes.numberedList}>
                    {waivers.map(({ id, title }, index) => (
                      <li key={id}>
                        {index + 1}. {title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <EventDrilldownSectionHeader title="Group Enrollment" onEdit={onEditGroups} />
              <div className={classes.groupEnrollmentCallout}>
                Volunteers are added to these groups during registration or when checking into this event.
              </div>
              {onboardingGroups.length === 0 ? (
                <div className={classes.detailsEmptyText}>None</div>
              ) : (
                <ul className={classes.numberedList}>
                  {onboardingGroups.map(({ groupId, name }, index) => (
                    <li key={groupId}>
                      {index + 1}. {name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className={classes.support} />
        </div>
      </EventExpansionPanelDetails>
    </EventExpansionPanel>
  )
}

const QuestionnaireRow = ({ order, name, onExport }: { order: number; name: string; onExport: () => void }) => {
  const classes = useEventDrilldownStyles()

  return (
    <li className={classes.questionnaireRow}>
      <span>
        {order}. {name}
      </span>
      <IconButton aria-label={`Download responses for ${name}`} size="small" onClick={onExport}>
        <SaveAltOutlinedIcon className={classes.downloadIcon} />
      </IconButton>
    </li>
  )
}
